// src/App.js
import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import GlobalOverview from './components/GlobalOverview';
import PMTracker from './components/PMTracker';
import CapacityPage from './components/CapacityPage';
import LoginPage from './components/LoginPage';
import TrackerBoard from './components/TrackerBoard';
import AdminPanel from './components/AdminPanel';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase/config';

function AppShell() {
  const { user, userProfile, loading, logout } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [showGlobal, setShowGlobal] = useState(false);
  const [showPM, setShowPM] = useState(false);
  const [showCapacity, setShowCapacity] = useState(false);
  const [activeTrackerUid, setActiveTrackerUid] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Pressing Backspace while focus isn't on a text field triggers the browser's
  // "navigate back" action in most browsers — that can silently exit the whole app
  // and discard unsaved edits. This blocks that everywhere except real editable fields.
  useEffect(() => {
    function guardBackspace(e) {
      if (e.key !== 'Backspace') return;
      const el = document.activeElement;
      const tag = el?.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable;
      if (!isEditable) e.preventDefault();
    }
    window.addEventListener('keydown', guardBackspace);
    return () => window.removeEventListener('keydown', guardBackspace);
  }, []);

  useEffect(() => {
    if (userProfile) {
      if (userProfile.role === 'viewer') {
        // Viewers default to null until users load; will be set after loadAllUsers
        setActiveTrackerUid(null);
      } else {
        setActiveTrackerUid(userProfile.uid);
      }
      loadAllUsers();
    }
  }, [userProfile?.uid]);

  useEffect(() => {
    if (userProfile?.role === 'viewer' && allUsers.length > 0 && !activeTrackerUid) {
      const firstUser = allUsers.find(u => u.role !== 'viewer');
      if (firstUser) setActiveTrackerUid(firstUser.uid);
    }
  }, [allUsers, userProfile?.role]);

  async function loadAllUsers() {
    setLoadingUsers(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      setAllUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    }
    setLoadingUsers(false);
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        background: '#f5f5f3', color: '#888',
      }}>
        Loading…
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const activeUser = allUsers.find(u => u.uid === activeTrackerUid) || userProfile;
  const isOwn = activeTrackerUid === userProfile?.uid;
  const isAdmin = userProfile?.role === 'admin';
  const isPMRole = userProfile?.role === 'pm';
  const isViewer = userProfile?.role === 'viewer';

  const myInitial = (userProfile?.displayName || userProfile?.email || '?')[0].toUpperCase();

  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
      background: '#eeede8',
    }}>
      {/* Top bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12,
        flexShrink: 0, height: 46,
      }}>
        {/* Brand */}
        <div style={{
          fontSize: 16, fontWeight: 900, letterSpacing: '-0.3px',
          background: 'linear-gradient(135deg, #185FA5, #6366f1)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          marginRight: 8,
        }}>
          AdFlowz
        </div>

        {/* Team tabs */}
        <div style={{ display: 'flex', gap: 2, flex: 1, overflowX: 'auto' }}>
          {/* My Tracker tab — hidden for viewers and PMs */}
          {userProfile && userProfile.role !== 'viewer' && userProfile.role !== 'pm' && userProfile.role !== 'supervisor' && (
            <TrackerTab
              uid={userProfile.uid}
              name="My Tracker"
              active={activeTrackerUid === userProfile.uid}
              isOwn={true}
              onClick={() => setActiveTrackerUid(userProfile.uid)}
            />
          )}
          {/* Team trackers — viewers/PMs see all non-viewers/non-PMs, users see everyone except themselves */}
          {allUsers
            .filter(u => u.role !== 'viewer' && u.role !== 'pm' && u.role !== 'supervisor')
            .filter(u => (userProfile?.role === 'viewer' || userProfile?.role === 'pm' || userProfile?.role === 'supervisor') ? true : u.uid !== userProfile?.uid)
            .map(u => (
              <TrackerTab
                key={u.uid}
                uid={u.uid}
                name={u.displayName || u.email}
                active={activeTrackerUid === u.uid}
                isOwn={false}
                onClick={() => setActiveTrackerUid(u.uid)}
              />
            ))
          }
        </div>

        {/* PM Tracker tab — visible to all logged-in users */}
        {userProfile && (
          <button
            onClick={() => { setShowPM(v => !v); setShowGlobal(false); setShowCapacity(false); }}
            style={{
              fontSize: 12, padding: '6px 14px',
              background: showPM ? '#EAF3DE' : 'transparent',
              color: showPM ? '#27500A' : '#666',
              border: 'none',
              borderBottom: showPM ? '2px solid #27500A' : '2px solid transparent',
              cursor: 'pointer', fontFamily: 'inherit',
              fontWeight: showPM ? 700 : 400,
              whiteSpace: 'nowrap',
            }}
          >
            📋 PM Tracker
          </button>
        )}

        {/* Capacity tab — visible to everyone */}
        <button
          onClick={() => { setShowCapacity(v => !v); setShowGlobal(false); setShowPM(false); }}
          style={{
            fontSize: 12, padding: '6px 14px',
            background: showCapacity ? '#EAF3DE' : 'transparent',
            color: showCapacity ? '#0F6E56' : '#666',
            border: 'none',
            borderBottom: showCapacity ? '2px solid #0F6E56' : '2px solid transparent',
            cursor: 'pointer', fontFamily: 'inherit',
            fontWeight: showCapacity ? 700 : 400,
            whiteSpace: 'nowrap',
          }}
        >
          📊 Capacity
        </button>

        {/* Global Overview tab */}
        <button
          onClick={() => { setShowGlobal(v => !v); setShowPM(false); setShowCapacity(false); }}
          style={{
            fontSize: 12, padding: '6px 14px',
            background: showGlobal ? '#E6F1FB' : 'transparent',
            color: showGlobal ? '#185FA5' : '#666',
            border: 'none',
            borderBottom: showGlobal ? '2px solid #185FA5' : '2px solid transparent',
            cursor: 'pointer', fontFamily: 'inherit',
            fontWeight: showGlobal ? 700 : 400,
            whiteSpace: 'nowrap',
          }}
        >
          🌐 Global Overview
        </button>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {isAdmin && (
            <button
              onClick={() => setShowAdmin(true)}
              style={{
                fontSize: 12, padding: '4px 10px',
                background: '#f5f5f3', border: '1px solid rgba(0,0,0,0.18)',
                borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', color: '#555',
              }}
            >
              👥 Team
            </button>
          )}
          {/* User avatar */}
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'linear-gradient(135deg, #185FA5, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            flexShrink: 0,
          }} title={userProfile?.email}>
            {myInitial}
          </div>
          <button
            onClick={logout}
            style={{
              fontSize: 12, padding: '4px 10px',
              background: '#f5f5f3', border: '1px solid rgba(0,0,0,0.18)',
              borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', color: '#555',
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Viewing-as banner */}
      {!isOwn && activeUser && userProfile?.role !== 'viewer' && (
        <div style={{
          background: '#E6F1FB', borderBottom: '1px solid #B5D4F4',
          padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 12, color: '#185FA5', flexShrink: 0,
        }}>
          <span>👁 Viewing <strong>{activeUser.displayName || activeUser.email}'s</strong> tracker</span>
          <button
            onClick={() => setActiveTrackerUid(userProfile.uid)}
            style={{
              fontSize: 11, padding: '2px 8px', background: '#185FA5', color: '#fff',
              border: 'none', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            ← Back to Mine
          </button>
        </div>
      )}
      {/* Viewer-mode banner */}
      {(userProfile?.role === 'viewer' || userProfile?.role === 'supervisor') && (
        <div style={{
          background: '#f5f5f3', borderBottom: '1px solid rgba(0,0,0,0.1)',
          padding: '5px 16px', display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 11, color: '#888', flexShrink: 0,
        }}>
          <span>{userProfile?.role === 'supervisor' ? '👁 Supervisor — view-only access (scoring settings editable)' : '👁 View-only access'}</span>
          {activeUser && <span>— viewing <strong>{activeUser.displayName || activeUser.email}'s</strong> tracker</span>}
        </div>
      )}

      {/* Content */}
      {showCapacity ? (
        <CapacityPage allUsers={allUsers} />
      ) : showGlobal ? (
        <GlobalOverview allUsers={allUsers} />
      ) : showPM ? (
        <PMTracker allUsers={allUsers} />
      ) : activeTrackerUid ? (
        <TrackerBoard
          key={activeTrackerUid}
          ownerUid={activeTrackerUid}
          ownerName={activeUser?.displayName || activeUser?.email || ''}
          isOwn={isOwn}
        />
      ) : null}

      {/* Admin Panel */}
      {showAdmin && <AdminPanel onClose={() => { setShowAdmin(false); loadAllUsers(); }} />}
    </div>
  );
}

function TrackerTab({ uid, name, active, isOwn, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 12, padding: '6px 14px',
        background: active ? '#E6F1FB' : 'transparent',
        color: active ? '#185FA5' : '#666',
        border: 'none',
        borderBottom: active ? '2px solid #185FA5' : '2px solid transparent',
        cursor: 'pointer', fontFamily: 'inherit',
        fontWeight: active ? 700 : 400,
        whiteSpace: 'nowrap',
        transition: 'all 0.15s',
      }}
    >
      {isOwn ? `★ ${name}` : name}
    </button>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
